/**
 * Shared presentation truth for InventoryItem.status.
 *
 * WHY THIS FILE EXISTS. Before S6b there were two German label maps for the
 * same field and they disagreed: the overview list and the CSV export read
 * `inventoryStatusLabelDe` (utils/inventoryExport.js), while the detail page
 * carried its own copy that was missing "retired" and "damaged" — so an
 * ausgemusterter Artikel showed the raw English word to the user. S6a pinned
 * that as a finding; S6b removes the divergence by making every surface read
 * the SAME label map and the SAME tone map.
 *
 * The LABEL lives in utils/inventoryExport.js and stays there: it is the map
 * the CSV export already owned, and moving it would churn a contract this
 * slice has no business touching. This module adds the missing half — the
 * tone — which until now lived privately inside InventoryList.jsx.
 *
 * TONE IS DELIBERATELY INCOMPLETE. Only the states the data model actually
 * stands behind get a colour. "repair" has no writer anywhere in the backend
 * (S2b), and the write-off semantics behind "disposed" are still unsettled, so
 * both stay neutral rather than being dressed up as a confident state. That is
 * the same judgement S5 made for the overview row; keeping it identical is the
 * point of sharing the map.
 */

const NEUTRAL = "border-border bg-muted text-muted-foreground";

const STATUS_TONE = {
  active: "border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  stored: NEUTRAL,
  damaged: "border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  retired: NEUTRAL,
};

/** Border/background/text classes for a status badge; neutral for anything unmapped. */
export function inventoryStatusToneClass(status) {
  return STATUS_TONE[String(status ?? "").trim()] || NEUTRAL;
}

export { NEUTRAL as INVENTORY_STATUS_NEUTRAL_TONE };
